import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useHasRole } from "@/hooks/useHasRole";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Plus, Star, Trash2, RefreshCw } from "lucide-react";
import { StripeSyncDialog } from "./StripeSyncDialog";

type StripeEnv = "test" | "live";
type Audience = "family" | "company";
type Interval = "month" | "year";

interface PriceRow {
  id: string;
  env: StripeEnv;
  audience: Audience;
  stripe_product_id: string;
  stripe_price_id: string;
  interval: Interval;
  is_default: boolean;
  is_active: boolean;
  label: string | null;
  notes: string | null;
}

interface CouponRow {
  id: string;
  env: StripeEnv;
  code: string;
  audience: Audience | null;
  description: string | null;
  is_active: boolean;
}

const emptyForm: Omit<PriceRow, "id"> = {
  env: "test",
  audience: "family",
  stripe_product_id: "",
  stripe_price_id: "",
  interval: "month",
  is_default: false,
  is_active: true,
  label: "",
  notes: "",
};

const emptyCoupon: Omit<CouponRow, "id"> = {
  env: "test",
  code: "",
  audience: null,
  description: "",
  is_active: true,
};

export default function AdminStripeConfigPage() {
  const hasRole = useHasRole("careos_admin");
  const roleLoading = hasRole === null;
  const [tab, setTab] = useState<StripeEnv>("test");
  const [rows, setRows] = useState<PriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [defaultEnv, setDefaultEnv] = useState<StripeEnv>("test");
  const [savingEnv, setSavingEnv] = useState(false);
  const [counts, setCounts] = useState<{ test: number; live: number }>({ test: 0, live: 0 });
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<PriceRow | null>(null);
  const [form, setForm] = useState<Omit<PriceRow, "id">>(emptyForm);
  const [saving, setSaving] = useState(false);

  // Coupons state
  const [coupons, setCoupons] = useState<CouponRow[]>([]);
  const [openCouponForm, setOpenCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<CouponRow | null>(null);
  const [couponForm, setCouponForm] = useState<Omit<CouponRow, "id">>(emptyCoupon);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);

  const filtered = useMemo(() => rows.filter((r) => r.env === tab), [rows, tab]);
  const filteredCoupons = useMemo(() => coupons.filter((c) => c.env === tab), [coupons, tab]);

  async function loadAll() {
    setLoading(true);
    const [{ data: priceData }, settingsRes, subsRes, { data: couponData }] = await Promise.all([
      supabase.from("stripe_price_config").select("*").order("audience").order("interval"),
      supabase.from("system_settings").select("value").eq("key", "default_stripe_env").maybeSingle(),
      supabase.from("subscriptions").select("stripe_env"),
      supabase.from("stripe_coupons").select("*").order("created_at", { ascending: false }),
    ]);
    setRows((priceData ?? []) as PriceRow[]);
    setCoupons((couponData ?? []) as CouponRow[]);
    let v = settingsRes.data?.value as unknown;
    if (typeof v === "string" && v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
    if (v === "live" || v === "test") setDefaultEnv(v);
    const subs = (subsRes.data ?? []) as { stripe_env: StripeEnv | null }[];
    setCounts({
      test: subs.filter((s) => s.stripe_env === "test").length,
      live: subs.filter((s) => s.stripe_env === "live").length,
    });
    setLoading(false);
  }

  useEffect(() => { void loadAll(); }, []);

  if (roleLoading) {
    return (
      <AppLayout>
        <div className="p-4 space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>
      </AppLayout>
    );
  }
  if (!hasRole) return <Navigate to="/app" replace />;

  function openNew() {
    setEditing(null);
    setForm({ ...emptyForm, env: tab });
    setOpenForm(true);
  }

  function openEdit(row: PriceRow) {
    setEditing(row);
    const { id: _id, ...rest } = row;
    setForm(rest);
    setOpenForm(true);
  }

  async function saveDefaultEnv(next: StripeEnv) {
    setSavingEnv(true);
    const { error } = await supabase
      .from("system_settings")
      .upsert({ key: "default_stripe_env", value: JSON.stringify(next) as unknown as never }, { onConflict: "key" });
    setSavingEnv(false);
    if (error) {
      toast.error("Falha ao salvar ambiente padrão", { description: error.message });
      return;
    }
    setDefaultEnv(next);
    toast.success(`Novos checkouts agora usam ${next.toUpperCase()}`);
  }

  async function submitForm() {
    if (!form.stripe_product_id.trim() || !form.stripe_price_id.trim()) {
      toast.error("Preencha o produto e o preço do Stripe");
      return;
    }
    setSaving(true);
    const payload = { ...form, label: form.label || null, notes: form.notes || null };
    const { error } = editing
      ? await supabase.from("stripe_price_config").update(payload).eq("id", editing.id)
      : await supabase.from("stripe_price_config").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Falha ao salvar", { description: error.message });
      return;
    }
    toast.success(editing ? "Preço atualizado" : "Preço cadastrado");
    setOpenForm(false);
    void loadAll();
  }

  async function toggleActive(row: PriceRow) {
    const { error } = await supabase
      .from("stripe_price_config")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) toast.error("Falha", { description: error.message });
    else void loadAll();
  }

  async function setAsDefault(row: PriceRow) {
    // Unset other defaults in same env+audience+interval, then set this one.
    await supabase
      .from("stripe_price_config")
      .update({ is_default: false })
      .eq("env", row.env)
      .eq("audience", row.audience)
      .eq("interval", row.interval);
    const { error } = await supabase
      .from("stripe_price_config")
      .update({ is_default: true })
      .eq("id", row.id);
    if (error) toast.error("Falha", { description: error.message });
    else {
      toast.success("Marcado como padrão");
      void loadAll();
    }
  }

  function copyCouponLink(code: string) {
    const url = `${window.location.origin}/app/subscription?coupon=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  }

  function openNewCoupon() {
    setEditingCoupon(null);
    setCouponForm({ ...emptyCoupon, env: tab });
    setOpenCouponForm(true);
  }

  function openEditCoupon(row: CouponRow) {
    setEditingCoupon(row);
    const { id: _id, ...rest } = row;
    setCouponForm(rest);
    setOpenCouponForm(true);
  }

  async function submitCoupon() {
    const code = couponForm.code.trim();
    if (!code) {
      toast.error("Informe o código do cupom");
      return;
    }
    setSavingCoupon(true);
    const payload = {
      env: couponForm.env,
      code,
      audience: couponForm.audience,
      description: couponForm.description?.trim() || null,
      is_active: couponForm.is_active,
    };
    const { error } = editingCoupon
      ? await supabase.from("stripe_coupons").update(payload).eq("id", editingCoupon.id)
      : await supabase.from("stripe_coupons").insert(payload);
    setSavingCoupon(false);
    if (error) {
      const isDuplicate =
        (error as { code?: string }).code === "23505" ||
        /duplicate key|stripe_coupons_env_code_uidx|already exists/i.test(error.message);
      if (isDuplicate) {
        toast.error("Cupom já cadastrado para este ambiente", {
          description: `O código "${code}" já existe em ${couponForm.env === "live" ? "Live" : "Test"}. Edite o cupom existente na lista.`,
        });
      } else {
        toast.error("Falha ao salvar cupom", { description: error.message });
      }
      return;
    }
    toast.success(editingCoupon ? "Cupom atualizado" : "Cupom cadastrado");
    setOpenCouponForm(false);
    void loadAll();
  }

  async function toggleCouponActive(row: CouponRow) {
    const { error } = await supabase
      .from("stripe_coupons")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) toast.error("Falha", { description: error.message });
    else void loadAll();
  }

  async function deleteCoupon(row: CouponRow) {
    if (!confirm(`Excluir cupom "${row.code}" (${row.env})?`)) return;
    const { error } = await supabase.from("stripe_coupons").delete().eq("id", row.id);
    if (error) toast.error("Falha", { description: error.message });
    else {
      toast.success("Cupom excluído");
      void loadAll();
    }
  }

  return (
    <AppLayout title="Configuração Stripe" showBack>
      <div className="p-4 space-y-4 max-w-6xl mx-auto">


        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Ambiente padrão</CardTitle></CardHeader>
            <CardContent>
              <Select value={defaultEnv} onValueChange={(v) => saveDefaultEnv(v as StripeEnv)} disabled={savingEnv}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Define para qual ambiente vão novos checkouts. Assinaturas existentes seguem o ambiente já gravado.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Assinaturas em test</CardTitle></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{counts.test}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Assinaturas em live</CardTitle></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{counts.live}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Cupons</CardTitle>
              <CardDescription>
                Cadastre cupons promocionais. O código deve existir no Stripe com as regras de desconto correspondentes.
              </CardDescription>
            </div>
            <Button onClick={openNewCoupon} size="sm">
              <Plus className="h-4 w-4 mr-2" />Cadastrar cupom
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-32 w-full" />
            ) : filteredCoupons.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Nenhum cupom cadastrado em {tab.toUpperCase()}.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Audiência</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Ativo</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCoupons.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono text-sm">{c.code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{c.audience ?? "todos"}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.description ?? "—"}</TableCell>
                      <TableCell>
                        <Switch checked={c.is_active} onCheckedChange={() => toggleCouponActive(c)} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-2">
                          <Button variant="outline" size="sm" className="min-w-[88px] justify-center" onClick={() => copyCouponLink(c.code)}>
                            <Copy className="h-3 w-3 mr-1" /> Link
                          </Button>
                          <Button variant="outline" size="sm" className="min-w-[88px] justify-center" onClick={() => openEditCoupon(c)}>Editar</Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteCoupon(c)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              Visualizando ambiente <b>{tab.toUpperCase()}</b>. Use as abas abaixo para alternar.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Catálogo de preços</CardTitle>
              <CardDescription>Cadastre preços ativos em cada ambiente</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setSyncOpen(true)} size="sm" variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />Importar do Stripe
              </Button>
              <Button onClick={openNew} size="sm"><Plus className="h-4 w-4 mr-2" />Cadastrar preço</Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={(v) => setTab(v as StripeEnv)}>
              <TabsList>
                <TabsTrigger value="test">Test</TabsTrigger>
                <TabsTrigger value="live">Live</TabsTrigger>
              </TabsList>
              <TabsContent value={tab} className="mt-4">
                {loading ? (
                  <Skeleton className="h-40 w-full" />
                ) : filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    Nenhum preço cadastrado em {tab.toUpperCase()}.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Audiência</TableHead>
                        <TableHead>Produto / Preço</TableHead>
                        <TableHead>Intervalo</TableHead>
                        <TableHead>Padrão</TableHead>
                        <TableHead>Ativo</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Badge variant="outline">{row.audience}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <div>{row.stripe_product_id}</div>
                            <div className="text-muted-foreground">{row.stripe_price_id}</div>
                          </TableCell>
                          <TableCell>{row.interval}</TableCell>
                          <TableCell>
                            {row.is_default ? (
                              <Badge><Star className="h-3 w-3 mr-1" />default</Badge>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => setAsDefault(row)}>
                                Tornar padrão
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <Switch checked={row.is_active} onCheckedChange={() => toggleActive(row)} />
                          </TableCell>
                          <TableCell className="text-sm">{row.label ?? "—"}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => openEdit(row)}>Editar</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar preço" : "Cadastrar preço"}</DialogTitle>
            <DialogDescription>
              Cadastre um produto e preço já existentes no Stripe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ambiente</Label>
                <Select value={form.env} onValueChange={(v) => setForm({ ...form, env: v as StripeEnv })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Audiência</Label>
                <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v as Audience })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">Família</SelectItem>
                    <SelectItem value="company">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Stripe Product ID</Label>
              <Input value={form.stripe_product_id} onChange={(e) => setForm({ ...form, stripe_product_id: e.target.value })} placeholder="prod_..." />
            </div>
            <div>
              <Label>Stripe Price ID</Label>
              <Input value={form.stripe_price_id} onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })} placeholder="price_..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Intervalo</Label>
                <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v as Interval })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Mensal</SelectItem>
                    <SelectItem value="year">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4 pt-6">
                <div className="flex items-center gap-2">
                  <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} />
                  <Label>Padrão</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                  <Label>Ativo</Label>
                </div>
              </div>
            </div>
            <div>
              <Label>Label (opcional)</Label>
              <Input value={form.label ?? ""} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Família — Mensal R$79,90" />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Cancelar</Button>
            <Button onClick={submitForm} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openCouponForm} onOpenChange={setOpenCouponForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCoupon ? "Editar cupom" : "Cadastrar cupom"}</DialogTitle>
            <DialogDescription>
              O código informado deve existir no Stripe (mesmo ambiente) com as regras de desconto desejadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ambiente</Label>
                <Select value={couponForm.env} onValueChange={(v) => setCouponForm({ ...couponForm, env: v as StripeEnv })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Audiência</Label>
                <Select
                  value={couponForm.audience ?? "all"}
                  onValueChange={(v) => setCouponForm({ ...couponForm, audience: v === "all" ? null : (v as Audience) })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="family">Família</SelectItem>
                    <SelectItem value="company">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Código do cupom</Label>
              <Input
                value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value })}
                placeholder="Ex.: BETA100"
              />
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Input
                value={couponForm.description ?? ""}
                onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                placeholder="Ex.: 100% off para beta testers"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={couponForm.is_active}
                onCheckedChange={(v) => setCouponForm({ ...couponForm, is_active: v })}
              />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCouponForm(false)}>Cancelar</Button>
            <Button onClick={submitCoupon} disabled={savingCoupon}>
              {savingCoupon ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <StripeSyncDialog open={syncOpen} onOpenChange={setSyncOpen} env={tab} onComplete={loadAll} />
    </AppLayout>
  );
}
